'use client';

import useAppErrorStore, { type AppErrorState } from '@/hooks/useAppErrorStore';
import Alert from '@/ui/alert';
import { Bug } from '@phosphor-icons/react/dist/ssr';
import { type ReactNode, useEffect, useRef } from 'react';
export default function AppError({
  extra
}: {
  extra?: ReactNode;
}) {
  const {
    error,
    resetError
  } = useAppErrorStore();
  if (!error) {
    return;
  }
  const {
    sentryId
  } = error;
  if (!sentryId) {
    return <ExpectedError error={error} extra={extra} resetError={resetError} />;
  }
  return <UnexpectedError error={error} extra={extra} resetError={resetError} data-sentry-element="UnexpectedError" data-sentry-component="AppError" data-sentry-source-file="app-error.tsx" />;
}
function ExpectedError({
  error,
  extra,
  resetError
}: {
  error: NonNullable<AppErrorState['error']>;
  extra?: ReactNode;
  resetError: () => void;
}) {
  const {
    friendlyError,
    friendlyErrorDetail
  } = error;
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [alertRef.current]);
  return <Alert variant="destructive" title={friendlyError} onDismiss={resetError} ref={alertRef} description={<>
          {friendlyErrorDetail}
          {extra}
        </>} data-sentry-element="Alert" data-sentry-component="ExpectedError" data-sentry-source-file="app-error.tsx" />;
}
function UnexpectedError({
  error,
  extra,
  resetError
}: {
  error: NonNullable<AppErrorState['error']>;
  extra?: ReactNode;
  resetError: () => void;
}) {
  const {
    friendlyError,
    displayError,
    sentryId,
    stack
  } = error;
  const isDev = process.env.NODE_ENV === 'development';
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [alertRef.current]);
  return <Alert variant="destructive" title={friendlyError} description={<>
          <p>An error occurred, please try again later.</p>
          {extra}
          <pre>
            <Bug className="inline" /> {sentryId}
          </pre>
          <p>Send us your feedback if this problem continues to occur.</p>
          {isDev && <div className="bg-neutral-200 border-l-3 border-neutral-600 p-4 mt-4 overflow-x-auto rounded-md">
              <p className="font-bold">Development ONLY debug information</p>
              <p>{displayError}</p>
              <pre className="overflow-x-auto">Stack: {stack}</pre>
            </div>}
        </>} onDismiss={resetError} ref={alertRef} data-sentry-element="Alert" data-sentry-component="UnexpectedError" data-sentry-source-file="app-error.tsx" />;
}